import React, { useCallback, useEffect, useRef } from "react";
import {
  IconTrash,
  IconPencil,
} from "@tabler/icons";
import _ from "lodash";
import { TextInput } from "@mantine/core";
import CreatableTypeSelect from "./creatableTypeSelect";
import InlineContactList from "./inlineContactList";
import { legacyEmailFieldsFromList } from "../helpers/contactListLegacySync";
import { saveFormFields } from "../helpers/formFieldSave";
import { DATA_EMAIL_TYPE } from "../data/options";
import { useCellRender } from "../hooks/useCellRender";
import { useTranslation } from "react-i18next";

const EMPTY_EMAIL_DRAFT = {
  type: "personal",
  value: "",
  tags: [],
  isDefault: false,
};

const TYPE_COLUMN_WIDTH = 160;

const isEmailDraftEmpty = (values) => !String(values?.value ?? "").trim();

const EmailList = ({ form: mainForm, name, readOnly, ...props }) => {
  const { t } = useTranslation();
  const emails = _.get(mainForm.values, name) ?? [];
  const [cellRender] = useCellRender();
  const listRef = useRef(null);

  const columns = [
    {
      field: "type",
      headerName: "Type",
      width: TYPE_COLUMN_WIDTH,
      cellRender: (col, data) => cellRender.ContactType(data.type),
    },
    {
      field: "value",
      headerName: "Email",
    },
    {
      field: "_actions",
      width: 72,
      isAction: true,
      actions: [
        {
          name: "edit",
          label: "Edit",
          icon: IconPencil,
          props: {},
        },
        {
          name: "delete",
          label: "Delete",
          icon: IconTrash,
          props: {},
        },
      ],
    },
  ];

  const validate = (values) => {
    const errors = {};
    if (!values.value?.trim()) {
      errors.value = t("Required");
    } else if (!/^\S+@\S+$/.test(values.value.trim())) {
      errors.value = t("Invalid email");
    }
    return errors;
  };

  const normalizeEntry = (values, existingRow) => {
    const id = existingRow?._id ?? values?._id;
    return {
      ...(id != null && id !== "" ? { _id: id } : {}),
      type: values.type?.trim() || "personal",
      value: values.value?.trim() ?? "",
      tags: existingRow?.tags ?? [],
      isDefault: Boolean(existingRow?.isDefault),
    };
  };

  const onChange = (values) => {
    saveFormFields(
      mainForm,
      {
        [name]: values,
        ...legacyEmailFieldsFromList(values, mainForm.values),
      },
      { notify: true }
    );
  };

  const flushIntoForm = useCallback(() => {
    const result = listRef.current?.commitPending?.() ?? { ok: true };
    if (!result.ok) return false;
    if (!result.rows) return { ok: true };
    const updates = {
      [name]: result.rows,
      ...legacyEmailFieldsFromList(result.rows, mainForm.values),
    };
    for (const [field, value] of Object.entries(updates)) {
      mainForm.setFieldValue(field, value);
    }
    return { ok: true, updates };
  }, [mainForm, name]);

  useEffect(() => {
    if (typeof mainForm.registerPendingEditor !== "function") return undefined;
    mainForm.registerPendingEditor(flushIntoForm);
    return () => mainForm.unregisterPendingEditor(flushIntoForm);
  }, [flushIntoForm, mainForm]);

  const renderFields = (form) => (
    <>
      <td
        style={{
          verticalAlign: "top",
          width: TYPE_COLUMN_WIDTH,
          minWidth: TYPE_COLUMN_WIDTH,
        }}
      >
          <CreatableTypeSelect
            form={form}
            name="type"
            items={DATA_EMAIL_TYPE}
            required
            size="xs"
          />
        </td>
        <td style={{ verticalAlign: "top" }}>
          <TextInput
            required
            size="xs"
            placeholder="john@example.com"
            {...form.getInputProps("value")}
          />
        </td>
    </>
  );

  return (
    <InlineContactList
      ref={listRef}
      rows={emails}
      readOnly={readOnly}
      onChange={onChange}
      columns={columns}
      emptyDraft={EMPTY_EMAIL_DRAFT}
      validate={validate}
      normalizeEntry={normalizeEntry}
      isDraftEmpty={isEmailDraftEmpty}
      renderFields={renderFields}
      {...props}
    />
  );
};

export default EmailList;
